import React from "react";
import moment from "moment-timezone";

const PageLevelViewPopup = ({
  isPageLevelViewPopup,
  selectedLanguage,
  latestUpdatedTime,
  dateFormatValueUpper,
  handleCloseVersionHisrtyPageLevel,
  pageLevelFilteredArr,
  timeZoneRef,
  applyTimeZoneToDisplay,
  approvedAccessRequests = [],
}) => {
  if (!isPageLevelViewPopup) return null;

// Cross-referencing and page filtering logic synchronized with Pascal/Camel case flexibility
  const enrichedApprovedRequests = (approvedAccessRequests || []).flatMap((request) => {
    const rows = [];
    const rawPages = request.approvedPages || request.ApprovedPages || [];
    const email = request.readerEmail || request.ReaderEmail || "";
    
    // Extract change flags from the base request object if they exist
    const isRequestChanged = request.isChanged === true || request.IsChanged === true;

    let matchedAnyRule = false;

    if (pageLevelFilteredArr && pageLevelFilteredArr.length > 0) {
      pageLevelFilteredArr.forEach((historyParent) => {
        const historicalRules =
          historyParent.pageLevelRestrictionHistoryDto ||
          historyParent.PageLevelRestrictionHistoryDto ||
          [];

        historicalRules.forEach((rule) => {
          const rulePages = (rule.pages || rule.Pages || []).map((p) => String(p));
          const matchedPages = rawPages.filter((page) => rulePages.includes(String(page)));

          if (matchedPages.length > 0) {
            matchedAnyRule = true;
            
            // Approval rows are highlighted based on whether the access request itself changed
            // (newly approved / partially approved / modified) - NOT on the matched restriction
            // rule's change flag, so a restriction-rule diff does not bleed into the approval rows.
            rows.push({
              id: `${request.id || request._id || Math.random()}-${rule.ruleNickname || Math.random()}`,
              approvedPages: matchedPages,
              category: rule.category || rule.Category || "Selected Readers",
              readerEmail: email,
              ruleNickname: rule.ruleNickname || rule.RuleNickname || "",
              isChanged: isRequestChanged
            });
          }
        });
      });
    }

    // Fallback logic
    if (!matchedAnyRule && rawPages.length > 0) {
      rows.push({
        id: request.id || request._id || `fallback-${Math.random()}`,
        approvedPages: rawPages,
        category: "Selected Readers", 
        readerEmail: email,
        ruleNickname: "",
        isChanged: isRequestChanged
      });
    }

    return rows;
  });

  // A document version is produced by a single action: either a restriction edit OR an
  // access-request approval. When the version's change is an approval, the approval rows carry
  // the yellow highlight and the restriction rows are shown WITHOUT color (the restriction rule
  // that changed is just the approval's side effect). When it's a restriction edit, the
  // restriction rows highlight and the approval rows are shown without color.
  const hasApprovalChange = (approvedAccessRequests || []).some(
    (r) => r.isChanged === true || r.IsChanged === true
  );

  return (
    <div className="child-popup" id="myModal">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        style={{ maxWidth: "800px" }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5
              className="d-flex align-items-center mb-0"
              id="staticBackdropLabel">
              {selectedLanguage.LstLanguageCodes.UI_PageRestrictions}
            </h5>
            <span style={{ fontWeight: "bold", marginLeft: "303px" }}>
              {selectedLanguage.LstLanguageCodes.UI_UpdateOn}:{" "}
              {latestUpdatedTime
                ? moment
                    .utc(
                      applyTimeZoneToDisplay(
                        new Date(latestUpdatedTime),
                        timeZoneRef?.current
                      )
                    )
                    .format(dateFormatValueUpper + " HH:mm")
                : "-"}
            </span>
            <button
              type="button"
              className="btn btn-outline-danger modalclose download"
              onClick={handleCloseVersionHisrtyPageLevel}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          <div
            className="modal-body"
            style={{ maxHeight: "60vh", overflow: "auto" }}>
            <table className="inertable page-restTh">
              <thead className="bg-lightblue">
                <tr>
                  <th className="borderRight">
                    {selectedLanguage.LstLanguageCodes.UI_Page}
                  </th>
                  <th className="borderRight">
                    {selectedLanguage.LstLanguageCodes.UI_RestrictionType}
                  </th>
                  <th className="borderRight">
                    {selectedLanguage.LstLanguageCodes.UI_ReadersList}
                  </th>
                  <th className="borderRight">
                    {selectedLanguage.LstLanguageCodes.UI_RuleNickname}
                  </th>
                  <th className="borderRight">
                    {selectedLanguage.LstLanguageCodes.UI_RequestAccess}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(pageLevelFilteredArr || []).map((record, index) =>
                  (record.pageLevelRestrictionHistoryDto || []).map(
                    (nestedRecord, nestedIndex) => (
                      <tr
                        className={`borderrow text-wrap-custom ${
                          !hasApprovalChange &&
                          (nestedRecord.isChanged === true ||
                            nestedRecord.IsChanged === true ||
                            record.isChanged === true ||
                            record.IsChanged === true)
                            ? " bgYellow"
                            : ""
                        } ${
                          nestedRecord.isDeleted === true ||
                          nestedRecord.IsDeleted === true
                            ? " foreColorRed"
                            : ""
                        }`}
                        key={`${record.id ?? index}-${(
                          nestedRecord.pages ||
                          nestedRecord.Pages ||
                          []
                        ).join("_")}-${nestedIndex}`}>
                        <td className="pcs">
                          {(nestedRecord.pages || nestedRecord.Pages || [])
                            .map((page) => `${page}`)
                            .join(", ")}
                        </td>

                        <td className="cur">
                          {(nestedRecord.category || nestedRecord.Category) ===
                          "Selected Readers"
                            ? selectedLanguage.LstLanguageCodes.UI_SelectedReaders
                            : (nestedRecord.category || nestedRecord.Category) ===
                              "Everyone Except Selected Readers"
                            ? selectedLanguage.LstLanguageCodes
                                .UI_EveryOneExceptSelected
                            : selectedLanguage.LstLanguageCodes.UI_NoReaders}{" "}
                          <div
                            style={{
                              display: "inline",
                              fontWeight: "600",
                            }}>
                            {nestedRecord.isDeleted === true ||
                            nestedRecord.IsDeleted === true
                              ? selectedLanguage.LstLanguageCodes.UI_Deleted
                              : ""}
                          </div>
                        </td>
                        <td className="per">
                          {((nestedRecord.category || nestedRecord.Category) ===
                            "Selected Readers" ||
                            (nestedRecord.category || nestedRecord.Category) ===
                              "Everyone Except Selected Readers") &&
                          (nestedRecord.readers || nestedRecord.Readers || [])
                            .length > 0
                            ? (nestedRecord.category ||
                                nestedRecord.Category) !== "No Readers" &&
                              (nestedRecord.readers || nestedRecord.Readers || [])
                                .map((reader) => `${reader}`)
                                .join(", ")
                            : selectedLanguage.LstLanguageCodes.UI_ApplicableToAll}
                        </td>
                        <td className="per WrapText w-20p">
                          {record.ruleNickname ??
                            record.RuleNickname ??
                            nestedRecord.ruleNickname ??
                            nestedRecord.RuleNickname}
                        </td>
                        <td>
                          {(record.allowAccessRequest ??
                            record.AllowAccessRequest ??
                            nestedRecord.allowAccessRequest ??
                            nestedRecord.AllowAccessRequest) === true
                            ? selectedLanguage.LstLanguageCodes.UI_Enabled
                            : selectedLanguage.LstLanguageCodes.UI_Disabled}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>

            {/* Approved Access Requests Table Section */}
            {enrichedApprovedRequests?.length > 0 && (
              <div className="approved-requests" style={{ marginTop: "20px" }}>
                <h4>{selectedLanguage.LstLanguageCodes.UI_ApprovedAccessRequests}</h4>
                <table
                  className="inertable page-restTh"
                  style={{ marginTop: "3px" }}>
                  <thead className="bg-lightblue">
                    <tr>
                      <th className="borderRight" style={{ minWidth: "4rem" }}>
                        {selectedLanguage.LstLanguageCodes.UI_Page}{" "}
                      </th>
                      <th className="borderRight">
                        {selectedLanguage.LstLanguageCodes.UI_RestrictionType}
                      </th>
                      <th className="borderRight">
                        {selectedLanguage.LstLanguageCodes.UI_ApprovedReaders}
                      </th>
                      <th className="borderRight">
                        {selectedLanguage.LstLanguageCodes.UI_RuleNickname}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedApprovedRequests.map((record) => (
                      <tr
                        className={`borderrow text-wrap-custom ${
                        record.isChanged ? " bgYellow" : ""
      }`}
                        key={record.id}>
                        <td className="pcs w-5p">
                          {record.approvedPages &&
                            [...record.approvedPages]
                              .sort((a, b) => a - b)
                              .join(", ")}
                        </td>
                        <td className="cur w-20p">
                          {record.category === "Selected Readers"
                            ? selectedLanguage.LstLanguageCodes.UI_SelectedReaders
                            : record.category ===
                              "Everyone Except Selected Readers"
                            ? selectedLanguage.LstLanguageCodes
                                .UI_EveryOneExceptSelected
                            : selectedLanguage.LstLanguageCodes.UI_NoReaders}
                        </td>
                        <td
                          className="per WrapText remail-text-ellipsis"
                          title={record.readerEmail}>
                          {record.readerEmail || "-"}
                        </td>
                        <td className="per WrapText w-20p">
                          {record.ruleNickname || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLevelViewPopup;